import React from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  ArrowForward,
  CheckCircleOutline,
  Home,
  Paid,
  Public,
  Headphones,
  Settings,
  CloudUpload,
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import Scanlines from '../ui/Scanlines';
import FeatureCard from '../ui/FeatureCard';

// Vinyl Record Animation (from HomePage)
const VinylRecord = ({ size = 300 }) => (
  <Box
    sx={{
      width: size,
      height: size,
      borderRadius: '50%',
      background: `
        radial-gradient(circle at center, 
          #FF6B1A 0%, 
          #FF6B1A 8%, 
          #1A1A1A 8%, 
          #1A1A1A 12%,
          #0D0D0D 12%,
          #0D0D0D 15%,
          #1A1A1A 15%,
          #1A1A1A 100%
        )
      `,
      position: 'relative',
      animation: 'spin 8s linear infinite',
      boxShadow: '0 0 60px rgba(255, 107, 26, 0.3)',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: '15%',
        left: '15%',
        right: '15%',
        bottom: '15%',
        borderRadius: '50%',
        background: 'repeating-radial-gradient(circle at center, transparent 0px, transparent 2px, rgba(255,255,255,0.03) 2px, rgba(255,255,255,0.03) 4px)',
      },
      '@keyframes spin': {
        '0%': { transform: 'rotate(0deg)' },
        '100%': { transform: 'rotate(360deg)' },
      },
    }}
  />
);

// FeatureCard replaced by imported FeatureCard



















































































// Tech Item
const TechItem = ({ icon, name }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 2,
      p: 2,
      bgcolor: 'background.paper',
      border: '1px solid', borderColor: 'secondary.light',
      transition: 'border-color 0.2s ease',
      '&:hover': {
        borderColor: 'primary.main',
      },
    }}
  >
    <Box sx={{ color: 'primary.main', '& svg': { fontSize: 24 } }}>{icon}</Box>
    <Typography sx={{ color: 'text.primary', fontWeight: 500 }}>{name}</Typography>
  </Box>
);

// Payout Highlight
const PayoutHighlight = () => (
  <Box
    sx={{
      p: 4,
      background: 'linear-gradient(135deg, rgba(255, 107, 26, 0.1) 0%, rgba(255, 107, 26, 0.05) 100%)',
      border: '2px solid #FF6B1A',
      textAlign: 'center',
    }}
  >
    <Typography
      sx={{
        fontFamily: '"Bebas Neue", sans-serif',
        fontSize: { xs: '4rem', md: '5rem' },
        color: 'primary.main',
        lineHeight: 1,
        textShadow: '0 0 40px rgba(255, 107, 26, 0.5)',
      }}
    >
      AGREED PAY
    </Typography>
    <Typography
      sx={{
        fontFamily: '"Space Mono", monospace',
        color: 'text.secondary',
        fontSize: '0.85rem',
        letterSpacing: '0.2em',
        textTransform: 'uppercase',
        mt: 1,
      }}
    >
      Booking Model
    </Typography>
    <Typography
      sx={{
        color: '#CCCCCC',
        mt: 2,
        lineHeight: 1.6,
      }}
    >
      Clear rates and transparent payment for every booked set.
    </Typography>
  </Box>
);

const DJsPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const benefits = [
    {
      icon: <Home />,
      title: 'Flexible Remote Sets',
      description:
        'Add remote sets from a familiar setup alongside your in-person calendar and local relationships.',
    },
    {
      icon: <Paid />,
      title: 'New Paid Programming',
      description:
        'Connect with venues seeking responsibly paid guest sets, off-night programming, and remote residencies.',
    },
    {
      icon: <Public />,
      title: 'Cross-Market Collaboration',
      description:
        'Share your sound with new rooms while keeping established local work at the center of your practice.',
    },
  ];

  const techStack = [
    { icon: <Headphones />, name: 'BUTT (Broadcast Using This Tool)' },
    { icon: <Settings />, name: 'Mixxx' },
    { icon: <CloudUpload />, name: 'Serato DJ' },
  ];

  return (
    <Box
      sx={{
        bgcolor: 'background.default',
        minHeight: '100vh',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <Scanlines />

      {/* Hero Section */}
      <Box
        sx={{
          pt: { xs: 16, md: 20 },
          pb: { xs: 10, md: 16 },
          position: 'relative',
        }}
      >
        {/* Floating Vinyl (Desktop Only) */}
        {!isMobile && (
          <Box
            sx={{
              position: 'absolute',
              right: '-5%',
              top: '50%',
              transform: 'translateY(-50%)',
              opacity: 0.4,
              zIndex: 0,
            }}
          >
            <VinylRecord size={400} />
          </Box>
        )}

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={7}>
              <Typography
                sx={{
                  fontFamily: '"Space Mono", monospace',
                  color: 'primary.main',
                  fontWeight: 700,
                  letterSpacing: '0.3em',
                  fontSize: '0.7rem',
                  textTransform: 'uppercase',
                  mb: 2,
                }}
              >
                For DJs
              </Typography>

              <Typography
                variant="h1"
                sx={{
                  color: 'text.primary',
                  mb: 3,
                  fontSize: { xs: '2.5rem', md: '3.5rem' },
                }}
              >
                Create New Paid Hours.{' '}
                <Box component="span" sx={{ color: 'primary.main' }}>
                  Reach New Rooms.
                </Box>
              </Typography>

              <Typography
                sx={{
                  color: 'text.secondary',
                  fontFamily: '"Space Mono", monospace',
                  mb: 5,
                  maxWidth: 550,
                  lineHeight: 1.8,
                  fontSize: { xs: '0.9rem', md: '1rem' },
                }}
              >
                Add remote residencies, guest sets, and cross-market collaborations
                alongside your local work. Build your following without giving up
                the gigs and relationships you already have.
              </Typography>

              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  size="large"
                  component={Link}
                  to="/register"
                  endIcon={<ArrowForward />}
                  sx={{ px: 4, py: 1.5 }}
                >
                  Apply as Founding DJ
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  component={Link}
                  to="/venues"
                  sx={{ px: 4, py: 1.5 }}
                >
                  For Venues
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Benefits Section */}
      <Box
        sx={{
          py: { xs: 10, md: 16 },
          bgcolor: 'secondary.dark',
          borderTop: '1px solid',
          borderBottom: '1px solid',
          borderColor: 'secondary.main',
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ mb: 8, textAlign: 'center' }}>
            <Typography
              sx={{
                fontFamily: '"Space Mono", monospace',
                color: 'primary.main',
                fontWeight: 700,
                letterSpacing: '0.3em',
                fontSize: '0.7rem',
                textTransform: 'uppercase',
                mb: 2,
              }}
            >
              Why SetSync
            </Typography>
            <Typography
              variant="h2"
              sx={{
                color: 'text.primary',
                fontSize: { xs: '2rem', md: '2.5rem' },
              }}
            >
              Built for Working DJs
            </Typography>
          </Box>

          <Grid container spacing={3}>
            {benefits.map((benefit, index) => (
              <Grid item xs={12} md={4} key={index}>
                <FeatureCard {...benefit} index={index} />
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Payout Section */}
      <Box sx={{ py: { xs: 10, md: 16 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={8} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography
                sx={{
                  fontFamily: '"Space Mono", monospace',
                  color: 'primary.main',
                  fontWeight: 700,
                  letterSpacing: '0.3em',
                  fontSize: '0.7rem',
                  textTransform: 'uppercase',
                  mb: 2,
                }}
              >
                Fair Pay
              </Typography>
              <Typography
                variant="h2"
                sx={{
                  color: 'text.primary',
                  mb: 4,
                  fontSize: { xs: '2rem', md: '2.5rem' },
                }}
              >
                Your Work. Clear Pay.
              </Typography>
              <Typography
                sx={{
                  color: 'text.secondary',
                  lineHeight: 1.8,
                  mb: 3,
                }}
              >
                SetSync’s booking model is designed around clear terms and transparent
                payment details from booking through payout.
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
                <CheckCircleOutline sx={{ color: 'primary.main', fontSize: 20, mt: 0.3 }} />
                <Typography sx={{ color: '#CCCCCC', lineHeight: 1.6 }}>
                  Agreed booking compensation does not depend on tips
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
                <CheckCircleOutline sx={{ color: 'primary.main', fontSize: 20, mt: 0.3 }} />
                <Typography sx={{ color: '#CCCCCC', lineHeight: 1.6 }}>
                  Our policy is that DJ-attributed tips go to the DJ, less only clearly disclosed payment-processing costs
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                <CheckCircleOutline sx={{ color: 'primary.main', fontSize: 20, mt: 0.3 }} />
                <Typography sx={{ color: '#CCCCCC', lineHeight: 1.6 }}>
                  Transparent payment and earnings details
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <PayoutHighlight />
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Tech Section */}
      <Box
        sx={{
          py: { xs: 10, md: 16 },
          bgcolor: 'secondary.dark',
          borderTop: '1px solid',
          borderBottom: '1px solid',
          borderColor: 'secondary.main',
        }}
      >
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography
              sx={{
                fontFamily: '"Space Mono", monospace',
                color: 'primary.main',
                fontWeight: 700,
                letterSpacing: '0.3em',
                fontSize: '0.7rem',
                textTransform: 'uppercase',
                mb: 2,
              }}
            >
              Bring Your Own Gear
            </Typography>
            <Typography
              variant="h2"
              sx={{
                color: 'text.primary',
                fontSize: { xs: '2rem', md: '2.5rem' },
                mb: 3,
              }}
            >
              Works With Your Setup
            </Typography>
            <Typography
              sx={{
                color: 'text.secondary',
                fontFamily: '"Space Mono", monospace',
                maxWidth: 600,
                mx: 'auto',
                lineHeight: 1.8,
              }}
            >
              Use the broadcast tools you already know. No proprietary software
              required.
            </Typography>
          </Box>

          <Grid container spacing={2}>
            {techStack.map((tech, index) => (
              <Grid item xs={12} sm={4} key={index}>
                <TechItem {...tech} />
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box sx={{ py: { xs: 12, md: 20 } }}>
        <Container maxWidth="md">
          <Box
            sx={{
              textAlign: 'center',
              p: { xs: 4, md: 8 },
              border: '2px solid', borderColor: 'secondary.light',
              backgroundColor: 'rgba(13, 13, 13, 0.9)',
              position: 'relative',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: -2,
                left: '10%',
                right: '10%',
                height: 4,
                bgcolor: 'primary.main',
              },
            }}
          >
            <Typography
              variant="h2"
              sx={{
                color: 'text.primary',
                mb: 3,
                fontSize: { xs: '2rem', md: '2.5rem' },
              }}
            >
              Join the Founding DJs
            </Typography>
            <Typography
              sx={{
                color: 'text.secondary',
                fontFamily: '"Space Mono", monospace',
                mb: 5,
                maxWidth: 500,
                mx: 'auto',
                lineHeight: 1.8,
              }}
            >
              We're onboarding our first 20 DJs. Get in early. Shape the
              platform. Build your legacy.
            </Typography>
            <Button
              variant="contained"
              size="large"
              component={Link}
              to="/register"
              endIcon={<ArrowForward />}
              sx={{ px: 5, py: 1.5 }}
            >
              Apply Now
            </Button>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default DJsPage;

